import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import { PhoneIcon } from '@heroicons/react/24/outline';

/**
 * A bottom bar for phones: the booking action plus a phone button.
 *
 * It appears once the hero's call to action has scrolled above the viewport and
 * goes away while the closing call to action is on screen, and stays away once
 * that call to action has scrolled above the viewport (the footer follows it, and
 * the bar must never sit over the footer's links or duplicate its booking link).
 * Two IntersectionObservers drive it; without IntersectionObserver the bar
 * simply never shows.
 *
 * - Phones only (under 768px). The header already carries a phone button there.
 * - Not mounted until shown, so it is not in the prerendered HTML and has no
 *   hidden focus stops. There is no focus trap: it is two ordinary links.
 * - Safe-area aware: the bottom padding adds `env(safe-area-inset-bottom)`.
 * - While shown it publishes `--sticky-cta-offset` on :root so the chat
 *   launcher (AIChat, which reads it under 768px only) rides above the bar
 *   instead of under it, and sets `scroll-padding-bottom` to the same value so
 *   keyboard focus is never scrolled to sit under the bar (WCAG 2.2 Focus Not
 *   Obscured). Both are removed again when the bar goes away.
 */
export interface StickyCtaBarProps {
  /** The bar shows once this has scrolled above the viewport. */
  heroRef: React.RefObject<Element>;
  /** The bar hides while this is on screen, and once it has scrolled above the viewport. */
  endRef: React.RefObject<Element>;
  cta: {
    buttonText: string;
    href: string;
    reloadDocument?: boolean;
    onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  };
  phone: { href: string; label: string; onClick?: () => void };
}

/** Bar height without the safe-area inset: 10px padding, a 50px control, 10px padding. */
const BAR_HEIGHT_PX = 70;

const rise = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const Bar = styled.div`
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 900;
  display: flex;
  gap: 10px;
  box-sizing: border-box;
  min-height: ${BAR_HEIGHT_PX}px;
  padding: 10px max(12px, env(safe-area-inset-right)) calc(10px + env(safe-area-inset-bottom, 0px))
    max(12px, env(safe-area-inset-left));
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(10px);
  box-shadow: 0 -1px 0 var(--davinci-cloud), 0 -6px 20px rgba(20, 53, 98, 0.12);
  animation: ${rise} 0.3s ease;

  @media (min-width: 768px) {
    display: none;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const control = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-height: 50px;
  border-radius: 12px;
  font-size: var(--fs-base);
  font-weight: var(--font-weight-bold);
  line-height: 1.25;
  text-align: center;
  text-decoration: none;

  &:focus-visible {
    outline: 3px solid var(--davinci-blue-fill);
    outline-offset: 2px;
  }
`;

const Book = styled(Link)`
  ${control}
  flex: 1;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, var(--color-accent-primary-fill), var(--color-accent-secondary));
  color: var(--white);

  &:hover {
    color: var(--white);
  }
`;

const Call = styled.a`
  ${control}
  flex: none;
  width: 50px;
  border: 2px solid var(--color-accent-primary-fill);
  background: var(--white);
  color: var(--color-accent-primary-fill);

  svg {
    width: 22px;
    height: 22px;
  }
`;

const StickyCtaBar: React.FC<StickyCtaBarProps> = ({ heroRef, endRef, cta, phone }) => {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    let heroGone = false;
    let endBlocking = false;
    const sync = () => setShown(heroGone && !endBlocking);

    const heroObserver = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      // "Gone" means scrolled past, not merely not yet reached.
      heroGone = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sync();
    });
    const endObserver = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      // On screen, or already scrolled past into the footer: either way the bar stays away.
      endBlocking = entry.isIntersecting || entry.boundingClientRect.top < 0;
      sync();
    });

    if (heroRef.current) heroObserver.observe(heroRef.current);
    if (endRef.current) endObserver.observe(endRef.current);
    return () => {
      heroObserver.disconnect();
      endObserver.disconnect();
    };
  }, [heroRef, endRef]);

  // The same inset the bar pads itself with, so the launcher clears the bar and the home
  // indicator, and a focused control is scrolled clear of the bar.
  useEffect(() => {
    if (!shown) return undefined;
    const root = document.documentElement;
    const height = `calc(${BAR_HEIGHT_PX}px + env(safe-area-inset-bottom, 0px))`;
    root.style.setProperty('--sticky-cta-offset', height);
    root.style.setProperty('scroll-padding-bottom', height);
    return () => {
      root.style.removeProperty('--sticky-cta-offset');
      root.style.removeProperty('scroll-padding-bottom');
    };
  }, [shown]);

  if (!shown) return null;

  return (
    <Bar role="region" aria-label="Quick actions">
      <Book to={cta.href} reloadDocument={cta.reloadDocument} onClick={cta.onClick}>
        {cta.buttonText}
      </Book>
      <Call href={phone.href} aria-label={phone.label} onClick={phone.onClick}>
        <PhoneIcon aria-hidden="true" />
      </Call>
    </Bar>
  );
};

export default StickyCtaBar;
